import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkContextMenuTrigger, CdkMenuTrigger } from '@angular/cdk/menu';
import { FullscreenOverlayContainer, Overlay, OverlayConfig, OverlayContainer, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfirmationDialogService } from '@shared/components/confirmation-dialog/confirmation-dialog.service';
import { CriterioTipoDado } from '@shared/criterio';
import { SliceCanTopicoDirective } from '@shared/infra/autorizacao/slice-can-topico.directive';
import { NormalizeString } from '@shared/util/normalize-string';
import { fromEvent } from 'rxjs';
import { DataManagerColuna, DataManagerColunaGrupo, DataManagerContent } from '../contracts/data-manager.model';
import { DataManagerService } from '../services/data-manager.service';
import { DataManagerGrupo } from '../state/data-manager-grupo';
import { DataManagerProxy } from '../state/data-manager-proxy';
import { DataManagerFiltroComponent } from './data-manager-filtro.component';
import { DataManagerGridListClient } from './data-manager-grid-list-client';
import { DatamanagerGrupoComponent } from './data-manager-grupo.component';
import { DataManagerMenuComponent } from './data-manager-menu.component';

@Component({
  selector: 'app-data-manager-grid-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './data-manager-grid-list.component.scss',
  templateUrl: './data-manager-grid-list.component.html',
  providers: [{ provide: OverlayContainer, useClass: FullscreenOverlayContainer }],
  imports: [
    CdkContextMenuTrigger,
    CdkDrag,
    CdkDropList,
    CdkMenuTrigger,
    CdkPortalOutlet,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
    DataManagerFiltroComponent,
    DatamanagerGrupoComponent,
    DataManagerMenuComponent,
    DatePipe,
    DecimalPipe,
    MatCheckbox,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    NgTemplateOutlet,
    SliceCanTopicoDirective,
    MatMenuTrigger,
  ],
})
export class DataManagerGridListComponent<T> {
  // Inputs
  client = input.required<DataManagerGridListClient<T>>();
  acaoToolbarTemplate = input<TemplateRef<any> | null>(null);
  acaoTabelaTemplate = input<TemplateRef<any> | null>(null);
  fullScreen = input(true);

  // Injectables
  private cdr = inject(ChangeDetectorRef);
  private confirmService = inject(ConfirmationDialogService);
  private destroyRef = inject(DestroyRef);
  private dmService = inject(DataManagerService);
  private overlay = inject(Overlay);
  private viewRef = inject(ViewContainerRef);

  // Private
  private contentTemplate = viewChild<TemplateRef<unknown>>('content');
  private overlayRef!: OverlayRef;
  private portalOutlet = viewChild(CdkPortalOutlet);
  private templatePortal!: TemplatePortal;
  private headerScrollEl = viewChild<ElementRef<HTMLElement>>('headerScrollEl');
  private overlayScrollEl = viewChild<ElementRef<HTMLElement>>('overlayScrollEl');

  private scrollViewport = viewChild(CdkVirtualScrollViewport);
  private viewportResizeObserver?: ResizeObserver;
  private sincronizandoScroll = false;
  private larguraTemporaria = signal<Record<string, number>>({});
  private redimensionando = signal(false);
  private readonly itemSizePadraoPx = 30;

  // Protected
  protected colunas = computed(
    () =>
      this.dmController()
        ?.colunas()
        .filter(
          (col: DataManagerColuna) =>
            (col.tipoColuna === 'dadoAdicional' &&
              col.sel &&
              col.colunaMaster.config.grupo === undefined &&
              !col.colunaMaster.config.ocultarColuna) ||
            (col.tipoColuna !== 'dadoAdicional' && col.config.grupo === undefined && !col.config.ocultarColuna),
        ) || [],
  );
  protected colunasSemDadoAdicional = computed(
    () =>
      (this.dmController()?.colunas() as DataManagerColunaGrupo[])
        .filter((col: DataManagerColuna) => col.tipoColuna !== 'dadoAdicional' && col.config.grupo === undefined)
        .sort((a, b) => NormalizeString(a.descricao).localeCompare(NormalizeString(b.descricao))) || [],
  );
  protected colunasTotais = computed(() => {
    const idsColunasVisiveis = new Set(this.colunas().map((coluna) => coluna.id));

    return (this.dmController()?.colunasTotais() || []).filter((coluna) => idsColunasVisiveis.has(coluna.id));
  });
  protected grupoColunasSpan = computed(() => Math.max(1, this.colunas().length - this.colunasTotais().length));
  protected linhasVisiveis = computed(() => this.dmController().conteudoVisivel());
  protected overlayPronto = signal(false);
  protected scrollIndexAtual = signal(0);
  protected scrollIndexAtualOffset = signal(0);
  protected itemSizePx = signal(this.itemSizePadraoPx);
  protected gruposOverlayAtivos = computed(() => {
    const linhas = this.linhasVisiveis();
    const grupos = this.dmController().grupos();
    if (linhas.length === 0 || grupos.length === 0) {
      return { gruposAtivos: [], proximoIgual: false };
    }

    const indexPrimeiroVisivel = Math.min(Math.max(this.scrollIndexAtual(), 0), linhas.length - 2);
    if (indexPrimeiroVisivel < 0) {
      return { gruposAtivos: [], proximoIgual: false };
    }

    const gruposAtivos: Array<DataManagerGrupo<T>> = [];

    const getGrupoNivel = (row: DataManagerContent<T>, nivel: number): DataManagerGrupo<T> | null => {
      if (row?.isGrupo && (row as DataManagerGrupo<T>).nivel === nivel) {
        return row as DataManagerGrupo<T>;
      }
      if (!row?.pai) {
        return null;
      }
      return getGrupoNivel(row.pai, nivel);
    };

    let nivel = 1;
    let rowIndex = indexPrimeiroVisivel;
    let row: DataManagerContent<T> | undefined = linhas[rowIndex];
    let grupoNivel: DataManagerGrupo<T> | null = null;
    while ((grupoNivel = getGrupoNivel(row, nivel)) !== null) {
      if (grupoNivel) {
        gruposAtivos.push(grupoNivel);
        nivel++;
        rowIndex++;
        row = linhas[rowIndex];
      }
    }

    const proximoIgual = linhas[rowIndex] && linhas[rowIndex].isGrupo;

    return { gruposAtivos, offset: proximoIgual ? this.scrollIndexAtualOffset() : 0 };
  });

  protected overlayScrollLeft = signal(0);
  protected gridTemplateDados = computed(() => {
    const template: string[] = [];

    if (this.selConfig) {
      template.push('3rem');
    }

    template.push(`${this.dmController().niveisGrupo}rem`);

    for (const coluna of this.colunas()) {
      template.push(`${this.getLarguraColuna(coluna)}rem`);
    }

    if (this.acaoTabelaTemplate()) {
      template.push('5rem');
    }

    return template.join(' ');
  });
  protected gridTemplateGrupo = computed(() => {
    const template: string[] = [];
    const colunasTotais = this.colunasTotais();
    const idsColunasTotais = new Set(colunasTotais.map((coluna) => coluna.id));
    const larguraColunasNaoTotais = this.colunas()
      .filter((coluna) => !idsColunasTotais.has(coluna.id))
      .reduce((total, coluna) => total + this.getLarguraColuna(coluna), 0);

    if (this.selConfig) {
      template.push('3rem');
    }

    template.push(`${this.dmController().niveisGrupo}rem`);
    template.push(`${Math.max(14, larguraColunasNaoTotais)}rem`);

    for (const coluna of colunasTotais) {
      template.push(`${this.getLarguraColuna(coluna)}rem`);
    }

    if (this.acaoTabelaTemplate()) {
      template.push('5rem');
    }

    return template.join(' ');
  });

  protected dmController = computed(() => {
    if (this.client().controller) {
      return this.client().controller!;
    }
    const tipo = this.client().tipo;
    const controller = this.dmService.getExistingController(tipo);
    return controller;
  });
  protected filtrando = computed(() => this.dmController().filtrando());
  protected fullScreenHabilitado = false;
  protected selection = computed(() => {
    return this.client().selecaoConfig?.objectoSelecao;
  });

  // Effects
  private dadosDisponiveis = effect(() => {
    const dadosOk = this.dmController().dadosDisponiveis();
    if (dadosOk) {
      setTimeout(() => {
        this.scrollViewport()?.scrollToIndex(dadosOk.scrollTop);
      });
    }
  });
  private refreshNecessario = effect(() => {
    const refresh = this.dmController().refreshNecessario() ? true : true;
    if (refresh) {
      setTimeout(() => {
        this.cdr.markForCheck();
      });
    }
  });
  private scrollChanged = effect(() => {
    this.scrollViewport()
      ?.scrolledIndexChange.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((valor: number) => {
        this.dmController().scrollTop = valor;
      });
  });

  // Lifecycle
  ngAfterViewInit(): void {
    this.templatePortal = new TemplatePortal(this.contentTemplate()!, this.viewRef);
    this.portalOutlet()?.attach(this.templatePortal);

    const overlayConfig = new OverlayConfig({
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      hasBackdrop: true,
      width: '98vw',
      height: '98vh',
      panelClass: 'slc-overlay-fullscreen',
    });

    this.overlayRef = this.overlay.create(overlayConfig);
    this.overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            this.alternarFullscreen(false);
          }
        },
      });

    const viewportEl = this.scrollViewport()?.elementRef.nativeElement as HTMLElement | undefined;
    if (viewportEl && typeof ResizeObserver !== 'undefined') {
      this.viewportResizeObserver = new ResizeObserver(() => this.atualizarCompensacaoScrollbarHeader());
      this.viewportResizeObserver.observe(viewportEl);
    }

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.atualizarCompensacaoScrollbarHeader());

    setTimeout(() => {
      this.atualizarCompensacaoScrollbarHeader();
      this.atualizarItemSizeViewport();
      const viewportScrollTop = this.scrollViewport()?.elementRef.nativeElement.scrollTop ?? 0;
      this.scrollIndexAtual.set(Math.floor(viewportScrollTop / Math.max(1, this.itemSizePx())));
      this.overlayScrollLeft.set(viewportEl?.scrollLeft || 0);
    });

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.viewportResizeObserver?.disconnect();
  }

  protected get selConfig() {
    return this.client().selecaoConfig;
  }

  protected alternarFullscreen(alternarPara?: boolean) {
    const scrollTop = this.dmController().scrollTop;

    this.fullScreenHabilitado = alternarPara === undefined ? !this.fullScreenHabilitado : alternarPara;
    if (this.fullScreenHabilitado) {
      this.portalOutlet()?.detach();
      this.overlayRef.attach(this.templatePortal);
    } else {
      this.portalOutlet()?.attach(this.templatePortal);
      this.overlayRef.detach();
    }

    setTimeout(() => {
      this.scrollViewport()?.scrollToIndex(scrollTop);
      this.atualizarCompensacaoScrollbarHeader();
      this.cdr.markForCheck();
    });
  }

  protected atualizarDados() {
    this.dmController().refresh();
  }

  protected onToggleOcultarColuna(coluna: DataManagerColunaGrupo) {
    this.dmController().toggleOcultarColuna(coluna, !coluna.config.ocultarColuna);
  }

  protected resetPreferencias() {
    this.confirmService
      .openDialog({
        titulo: 'Limpeza das Preferências do Usuário',
        mensagem: `Confirma a limpeza das Preferências do Usuário?
      A configuração padrão será carregada.`,
      })
      .subscribe((resp: any) => {
        if (resp.resposta === 'S') {
          this.dmController().resetPreferencias();
        }
      });
  }

  protected dropColuna(event: CdkDragDrop<any, any, DataManagerColuna>) {
    const colunaDestino = event.container.getSortedItems()[event.currentIndex].data;
    if (colunaDestino) {
      this.dmController()?.colunaAlterarOrdem(event.item.data, colunaDestino);
    }
  }

  protected dropColunaSortPredicate(
    index: number,
    colunaOrigem: CdkDrag<DataManagerColuna>,
    drop: CdkDropList<DataManagerColuna[]>,
  ) {
    const indiceColunaOrigem = drop.data.findIndex((d: DataManagerColuna) => d === colunaOrigem.data);
    if (indiceColunaOrigem === -1) {
      return true;
    }
    const colunaDestino = indiceColunaOrigem > index ? drop.data[index] : drop.data[index + 1];
    return !colunaDestino || colunaDestino.tipoColuna !== 'dadoAdicional' || !colunaDestino.sel;
  }

  protected isGrupo(row: DataManagerContent<T>) {
    return row.isGrupo;
  }

  protected gridTemplateLinha(_row: DataManagerContent<T>) {
    return this.gridTemplateDados();
  }

  protected onLinhaClick(row: DataManagerContent<T>) {
    if (row.isGrupo) {
      this.dmController().grupoAlternarExpandir(row as DataManagerGrupo<T>);
    }
  }

  protected onHeaderScroll(event: Event) {
    if (this.sincronizandoScroll) {
      return;
    }

    const target = event.target as HTMLElement;
    const viewportEl = this.scrollViewport()?.elementRef.nativeElement as HTMLElement | undefined;
    if (!viewportEl) {
      return;
    }

    this.sincronizandoScroll = true;
    viewportEl.scrollLeft = target.scrollLeft;
    this.sincronizandoScroll = false;
    this.overlayScrollLeft.set(target.scrollLeft);
  }

  protected onViewportScroll(event: Event) {
    if (this.sincronizandoScroll) {
      return;
    }

    const target = event.target as HTMLElement;
    const headerEl = this.headerScrollEl()?.nativeElement;
    if (!headerEl) {
      return;
    }

    const overlayEl = this.overlayScrollEl()?.nativeElement;
    if (!overlayEl) {
      return;
    }

    this.atualizarCompensacaoScrollbarHeader();
    this.atualizarItemSizeViewport();
    this.scrollIndexAtual.set(Math.floor(target.scrollTop / Math.max(1, this.itemSizePx())));
    this.scrollIndexAtualOffset.set(-(target.scrollTop % this.itemSizePx()));

    this.sincronizandoScroll = true;
    headerEl.scrollLeft = target.scrollLeft;
    overlayEl.scrollLeft = target.scrollLeft;
    this.sincronizandoScroll = false;
    this.overlayScrollLeft.set(target.scrollLeft);
  }

  protected onSelecionarTodos() {
    this.selConfig?.selecaoSelecionarTodos();
  }

  protected selecaoTodosSelecionados() {
    return this.selConfig?.selecaoTodosSelecionados() || false;
  }

  protected selecaoIndeterminada() {
    return this.selConfig?.selecaoIndeterminada() || false;
  }

  protected selecaoHabilitada(row: DataManagerContent<T>) {
    return this.selConfig?.selecaoHabilitada(row) || false;
  }

  protected onSelecionarTodosGrupo(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    if (!grupo) {
      return;
    }
    this.selConfig?.selecaoSelecionarTodos(grupo);
  }

  protected selecaoTodosSelecionadosGrupo(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    if (!grupo) {
      return false;
    }
    return this.selConfig?.selecaoTodosSelecionados(grupo) || false;
  }

  protected selecaoIndeterminadaGrupo(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    if (!grupo) {
      return false;
    }
    return this.selConfig?.selecaoIndeterminada(grupo) || false;
  }

  protected grupoNivel(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    return grupo?.nivel || 1;
  }

  protected grupoExpandido(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    return grupo?.expandido || false;
  }

  protected grupoColuna(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    return grupo?.coluna;
  }

  protected grupoDescricao(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    return grupo?.descricao || '';
  }

  protected grupoTotalItens(row: DataManagerContent<T>) {
    const grupo = this.asGrupo(row);
    return grupo?.totalItens || 0;
  }

  protected grupoTotalColuna(row: DataManagerContent<T>, coluna: DataManagerColunaGrupo) {
    const grupo = this.asGrupo(row);
    if (!grupo) {
      return null;
    }

    return grupo.getTotal(coluna);
  }

  protected getValorLinha(row: DataManagerContent<T>, coluna: DataManagerColuna) {
    if (row.isGrupo) {
      return null;
    }

    return (row as DataManagerProxy<T>).get(coluna);
  }

  protected getLarguraColuna(coluna: DataManagerColuna | DataManagerColunaGrupo) {
    const larguraTemp = this.larguraTemporaria()[coluna.id];
    if (larguraTemp && larguraTemp > 0) {
      return larguraTemp;
    }

    const larguraConfig = (coluna as any)?.config?.largura;
    if (larguraConfig && larguraConfig > 0) {
      return larguraConfig;
    }

    if (coluna.tipoDado === CriterioTipoDado.valor || coluna.tipoDado === CriterioTipoDado.numerico) {
      return 10;
    }

    if (coluna.tipoDado === CriterioTipoDado.data) {
      return 11;
    }

    return 14;
  }

  protected dragDesabilitado(coluna: DataManagerColuna) {
    return this.redimensionando() || coluna.tipoColuna === 'dadoAdicional';
  }

  protected onResizeStart(event: MouseEvent, coluna: DataManagerColuna) {
    event.preventDefault();
    event.stopPropagation();
    this.redimensionando.set(true);

    const larguraInicial = this.getLarguraColuna(coluna);
    const startX = event.clientX;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaPx = moveEvent.clientX - startX;
      const rootFontSizePx = this.getRootFontSizePx();
      const deltaRem = deltaPx / rootFontSizePx;
      // mínimo = padding (2rem) + meta (3rem + 0.25rem gap) + título mínimo (3rem) ≈ 8.5rem
      const novaLargura = Math.max(8.5, Math.round((larguraInicial + deltaRem) * 10) / 10);

      this.larguraTemporaria.update((valorAtual) => ({
        ...valorAtual,
        [coluna.id]: novaLargura,
      }));
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      const larguraFinal = this.larguraTemporaria()[coluna.id] || larguraInicial;
      this.dmController().colunaAlterarLargura(coluna, larguraFinal);

      this.larguraTemporaria.update((valorAtual) => {
        const next = { ...valorAtual };
        delete next[coluna.id];
        return next;
      });

      this.redimensionando.set(false);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  protected onResizeDoubleClick(event: MouseEvent, coluna: DataManagerColuna) {
    event.preventDefault();
    event.stopPropagation();

    const larguraAutoFit = this.calcularLarguraAutoFit(coluna);
    this.dmController().colunaAlterarLargura(coluna, larguraAutoFit);

    this.larguraTemporaria.update((valorAtual) => {
      const next = { ...valorAtual };
      delete next[coluna.id];
      return next;
    });
  }

  protected trackByLinha(index: number, row: DataManagerContent<T>) {
    if (row.isGrupo) {
      return `grupo-${(row as DataManagerGrupo<T>).key}`;
    }

    const proxy = row as DataManagerProxy<T>;
    return `item-${proxy.id || index}`;
  }

  private asGrupo(row: DataManagerContent<T>) {
    if (!row.isGrupo) {
      return null;
    }
    return row as DataManagerGrupo<T>;
  }

  private getRootFontSizePx() {
    const fontSize = getComputedStyle(document.documentElement).fontSize;
    const parsed = Number.parseFloat(fontSize);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
  }

  private atualizarCompensacaoScrollbarHeader() {
    const shellEl = this.headerScrollEl()?.nativeElement.parentElement as HTMLElement | null;
    const headerEl = this.headerScrollEl()?.nativeElement;
    const viewportEl = this.scrollViewport()?.elementRef.nativeElement as HTMLElement | undefined;
    if (!headerEl || !viewportEl) {
      return;
    }

    const scrollbarWidth = Math.max(0, viewportEl.offsetWidth - viewportEl.clientWidth);
    headerEl.style.setProperty('--slc-grid-scrollbar-width', `${scrollbarWidth}px`);
    shellEl?.style.setProperty('--slc-grid-scrollbar-width', `${scrollbarWidth}px`);
    shellEl?.style.setProperty('--slc-grid-header-height', `${headerEl.offsetHeight}px`);
    this.overlayPronto.set(true);
  }

  private atualizarItemSizeViewport() {
    const viewportEl = this.scrollViewport()?.elementRef.nativeElement as HTMLElement | undefined;
    if (!viewportEl) {
      return;
    }

    const valorCss = getComputedStyle(viewportEl).getPropertyValue('--mat-table-row-item-container-height').trim();
    const itemSize = Number.parseFloat(valorCss);
    if (Number.isFinite(itemSize) && itemSize > 0) {
      this.itemSizePx.set(itemSize);
      return;
    }

    const primeiraLinha = viewportEl.querySelector('.slc-grid-row') as HTMLElement | null;
    const alturaLinha = primeiraLinha?.offsetHeight;
    if (alturaLinha && alturaLinha > 0) {
      this.itemSizePx.set(alturaLinha);
      return;
    }

    this.itemSizePx.set(this.itemSizePadraoPx);
  }

  private calcularLarguraAutoFit(coluna: DataManagerColuna) {
    // const headerChars = (coluna.descricao || '').trim().length;
    const headerChars = (coluna.descricao || '').split(' ').reduce((max, palavra) => Math.max(max, palavra.length), 0);

    const headerCharsLimitado = Math.min(headerChars, 20);
    let maxChars = Math.max(headerCharsLimitado, 8);

    const amostra = this.linhasVisiveis();
    for (const row of amostra) {
      if (row.isGrupo) {
        continue;
      }

      const valor = (row as DataManagerProxy<T>).get(coluna);
      const texto = this.valorParaTexto(valor, coluna.tipoDado);
      if (texto.length > maxChars) {
        maxChars = texto.length;
      }
    }

    if (coluna.templateDados) {
      maxChars = Math.max(maxChars, 16);
    }

    const larguraRem = Math.round((maxChars * 0.62 + 3.5) * 10) / 10;
    return Math.min(48, Math.max(6, larguraRem));
  }

  private valorParaTexto(valor: unknown, tipoDado: CriterioTipoDado | undefined) {
    if (valor === null || valor === undefined) {
      return '';
    }

    if (tipoDado === CriterioTipoDado.data) {
      const data = valor instanceof Date ? valor : new Date(valor as any);
      return Number.isNaN(data.getTime()) ? String(valor) : data.toLocaleDateString('pt-PT');
    }

    if (tipoDado === CriterioTipoDado.valor || tipoDado === CriterioTipoDado.numerico) {
      const numero = typeof valor === 'number' ? valor : Number(valor);
      return Number.isNaN(numero)
        ? String(valor)
        : new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 }).format(numero);
    }

    return String(valor);
  }
}
